import React, { useState, useCallback, useEffect } from 'react';
import {
  Container, Typography, Box, Button, CircularProgress, Alert, Paper, Grid, TextField, 
  Dialog, DialogActions, DialogContent, DialogTitle, Switch, FormControlLabel,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Chip,
  Divider, Card, CardContent
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from './supabaseClient';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

const LiveAgentAdmin = () => {
  const [liveAgentProduct, setLiveAgentProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Subscription statistics
  const [subscriptionStats, setSubscriptionStats] = useState({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    totalSeats: 0,
    totalRevenue: 0,
  });
  const [loadingStats, setLoadingStats] = useState(false);

  const initialFormState = {
    name: 'Live Human Agent',
    description: 'Live human support via WhatsApp, Web Chat, Instagram, Messenger, and Telegram. Each seat allows one human agent to handle customer inquiries in real-time.',
    price_per_seat: 19.00,
    stripe_price_id: '',
    stripe_product_id: '',
    is_active: true,
    max_seats_per_company: 100,
    min_seats: 1,
  };

  const [formData, setFormData] = useState(initialFormState);

  // Fetch Live Agent product configuration
  const fetchLiveAgentProduct = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('live_agent_products')
        .select('*')
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No product found - not created yet
          setLiveAgentProduct(null);
        } else {
          throw fetchError;
        }
      } else {
        setLiveAgentProduct(data);
      }
    } catch (err) {
      console.error('Error fetching live agent product:', err);
      setError(err.message || 'Failed to fetch Live Agent product configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch subscription statistics
  const fetchSubscriptionStats = useCallback(async () => {
    setLoadingStats(true);

    try {
      // Get all subscriptions
      const { data: subscriptions, error: subsError } = await supabase
        .from('company_live_agent_subscriptions')
        .select('seat_count, status');

      if (subsError) throw subsError;

      // Calculate stats
      const stats = {
        totalSubscriptions: subscriptions.length,
        activeSubscriptions: subscriptions.filter(s => s.status === 'active').length,
        totalSeats: subscriptions
          .filter(s => s.status === 'active')
          .reduce((sum, s) => sum + (s.seat_count || 0), 0),
        totalRevenue: subscriptions
          .filter(s => s.status === 'active')
          .reduce((sum, s) => sum + ((s.seat_count || 0) * (liveAgentProduct?.price_per_seat || 19)), 0),
      };

      setSubscriptionStats(stats);
    } catch (err) {
      console.error('Error fetching subscription stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [liveAgentProduct]);

  useEffect(() => {
    fetchLiveAgentProduct();
  }, [fetchLiveAgentProduct]);

  useEffect(() => {
    if (liveAgentProduct) {
      fetchSubscriptionStats();
    }
  }, [liveAgentProduct, fetchSubscriptionStats]);

  // Handle Create Product
  const handleOpenCreateDialog = () => {
    setFormData(initialFormState);
    setCreateError(null);
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setFormData(initialFormState);
    setCreateError(null);
  };

  const handleSubmitCreate = async () => {
    setIsCreating(true);
    setCreateError(null);

    try {
      const payload = {
        ...formData,
        price_per_seat: parseFloat(formData.price_per_seat),
        max_seats_per_company: parseInt(formData.max_seats_per_company, 10),
        min_seats: parseInt(formData.min_seats, 10),
      };

      const { data, error: createErr } = await supabase
        .from('live_agent_products')
        .insert([payload])
        .select()
        .single();

      if (createErr) throw createErr;

      setLiveAgentProduct(data);
      handleCloseCreateDialog();
      
      // Show success message (you can add a snackbar here)
      console.log('Live Agent product created successfully');
    } catch (err) {
      console.error('Error creating Live Agent product:', err);
      setCreateError(err.message || 'Failed to create Live Agent product');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle Edit Product
  const handleOpenEditDialog = () => {
    if (liveAgentProduct) {
      setFormData({
        name: liveAgentProduct.name || '',
        description: liveAgentProduct.description || '',
        price_per_seat: liveAgentProduct.price_per_seat || 19.00,
        stripe_price_id: liveAgentProduct.stripe_price_id || '',
        stripe_product_id: liveAgentProduct.stripe_product_id || '',
        is_active: liveAgentProduct.is_active !== false,
        max_seats_per_company: liveAgentProduct.max_seats_per_company || 100,
        min_seats: liveAgentProduct.min_seats || 1,
      });
      setUpdateError(null);
      setOpenEditDialog(true);
    }
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setFormData(initialFormState);
    setUpdateError(null);
  };

  const handleSubmitUpdate = async () => {
    setIsUpdating(true);
    setUpdateError(null);

    try {
      const payload = {
        ...formData,
        price_per_seat: parseFloat(formData.price_per_seat),
        max_seats_per_company: parseInt(formData.max_seats_per_company, 10),
        min_seats: parseInt(formData.min_seats, 10),
        updated_at: new Date().toISOString(),
      };

      const { data, error: updateErr } = await supabase
        .from('live_agent_products')
        .update(payload)
        .eq('id', liveAgentProduct.id)
        .select()
        .single();

      if (updateErr) throw updateErr;

      setLiveAgentProduct(data);
      handleCloseEditDialog();
      
      console.log('Live Agent product updated successfully');
    } catch (err) {
      console.error('Error updating Live Agent product:', err);
      setUpdateError(err.message || 'Failed to update Live Agent product');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle Delete Product
  const handleOpenDeleteDialog = () => {
    setDeleteError(null);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteError(null);
  };

  const handleSubmitDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      // Check if there are active subscriptions
      const { data: activeSubs, error: checkError } = await supabase
        .from('company_live_agent_subscriptions')
        .select('id')
        .eq('status', 'active')
        .limit(1);

      if (checkError) throw checkError;

      if (activeSubs && activeSubs.length > 0) {
        setDeleteError('Cannot delete: There are active subscriptions. Please cancel all subscriptions first.');
        setIsDeleting(false);
        return;
      }

      const { error: deleteErr } = await supabase
        .from('live_agent_products')
        .delete()
        .eq('id', liveAgentProduct.id);

      if (deleteErr) throw deleteErr;

      setLiveAgentProduct(null);
      handleCloseDeleteDialog();
      
      console.log('Live Agent product deleted successfully');
    } catch (err) {
      console.error('Error deleting Live Agent product:', err);
      setDeleteError(err.message || 'Failed to delete Live Agent product');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle Toggle Active
  const handleToggleActive = async () => {
    if (!liveAgentProduct) return;

    try {
      const newActiveState = !liveAgentProduct.is_active;

      const { data, error: updateErr } = await supabase
        .from('live_agent_products')
        .update({ 
          is_active: newActiveState,
          updated_at: new Date().toISOString()
        })
        .eq('id', liveAgentProduct.id)
        .select()
        .single();

      if (updateErr) throw updateErr;

      setLiveAgentProduct(data);
      console.log(`Live Agent product ${newActiveState ? 'activated' : 'deactivated'}`);
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError(err.message || 'Failed to update status');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Container>
        <CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        {/* Header */}
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4" gutterBottom>
              Manage Live Human Agent Product
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Per-seat pricing for live human support agents
            </Typography>
          </Grid>
          <Grid item>
            {!liveAgentProduct && (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleOpenCreateDialog}
                startIcon={<PeopleIcon />}
              >
                Create Live Agent Product
              </Button>
            )}
          </Grid>
        </Grid>
        
        <Button component={RouterLink} to="/admin" variant="outlined" sx={{ mb: 3 }}>
          Back to Admin Panel
        </Button>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Statistics Cards */}
        {liveAgentProduct && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Active Subscriptions
                  </Typography>
                  <Typography variant="h4">
                    {loadingStats ? '...' : subscriptionStats.activeSubscriptions}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    of {subscriptionStats.totalSubscriptions} total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Active Seats
                  </Typography>
                  <Typography variant="h4">
                    {loadingStats ? '...' : subscriptionStats.totalSeats}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    agent seats
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Monthly Revenue
                  </Typography>
                  <Typography variant="h4">
                    ${loadingStats ? '...' : subscriptionStats.totalRevenue.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    per month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Price Per Seat
                  </Typography>
                  <Typography variant="h4">
                    ${liveAgentProduct.price_per_seat}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    per month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Product Configuration */}
        {liveAgentProduct ? (
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs>
                <Typography variant="h5" gutterBottom>
                  {liveAgentProduct.name}
                </Typography>
              </Grid>
              <Grid item>
                <Chip 
                  label={liveAgentProduct.is_active ? 'Active' : 'Inactive'}
                  color={liveAgentProduct.is_active ? 'success' : 'default'}
                  sx={{ mr: 1 }}
                />
                <IconButton 
                  onClick={handleToggleActive}
                  color={liveAgentProduct.is_active ? 'success' : 'default'}
                  title={liveAgentProduct.is_active ? 'Deactivate' : 'Activate'}
                >
                  {liveAgentProduct.is_active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                </IconButton>
                <IconButton 
                  onClick={handleOpenEditDialog}
                  color="primary"
                  title="Edit"
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  onClick={handleOpenDeleteDialog}
                  color="error"
                  title="Delete"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>

            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell>{liveAgentProduct.description}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Price Per Seat</strong></TableCell>
                    <TableCell>${liveAgentProduct.price_per_seat} USD per month</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Min Seats</strong></TableCell>
                    <TableCell>{liveAgentProduct.min_seats}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Max Seats Per Company</strong></TableCell>
                    <TableCell>{liveAgentProduct.max_seats_per_company}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Stripe Product ID</strong></TableCell>
                    <TableCell>
                      <code>{liveAgentProduct.stripe_product_id || 'Not set'}</code>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Stripe Price ID</strong></TableCell>
                    <TableCell>
                      <code>{liveAgentProduct.stripe_price_id || 'Not set'}</code>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Created At</strong></TableCell>
                    <TableCell>
                      {new Date(liveAgentProduct.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Last Updated</strong></TableCell>
                    <TableCell>
                      {new Date(liveAgentProduct.updated_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Setup Instructions
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" paragraph>
                <strong>Important:</strong> The unit label "seat" cannot be added via API. You must create the price in Stripe Dashboard.
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Steps:</strong>
                <ol>
                  <li>Go to Stripe Dashboard → Products</li>
                  <li>Find or create "Live Human Agent" product</li>
                  <li>Add new price: ${liveAgentProduct.price_per_seat} USD, Monthly</li>
                  <li>Expand "Advanced" section</li>
                  <li>Set "Unit label" to: <code>seat</code></li>
                  <li>Copy the Price ID and Product ID</li>
                  <li>Click Edit button above and paste the IDs</li>
                </ol>
              </Typography>
            </Alert>
          </Paper>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Live Agent Product Configured
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Create a Live Human Agent product to enable per-seat pricing for live support agents.
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleOpenCreateDialog}
              startIcon={<PeopleIcon />}
            >
              Create Live Agent Product
            </Button>
          </Paper>
        )}
      </Box>

      {/* Create Dialog */}
      <Dialog 
        open={openCreateDialog} 
        onClose={handleCloseCreateDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Create Live Agent Product</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          
          <TextField
            label="Product Name"
            fullWidth
            margin="normal"
            value={formData.name}
            onChange={(e) => handleFormChange('name', e.target.value)}
            required
          />
          
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
          />
          
          <TextField
            label="Price Per Seat (USD)"
            type="number"
            fullWidth
            margin="normal"
            value={formData.price_per_seat}
            onChange={(e) => handleFormChange('price_per_seat', e.target.value)}
            required
            inputProps={{ step: '0.01', min: '0' }}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Minimum Seats"
                type="number"
                fullWidth
                margin="normal"
                value={formData.min_seats}
                onChange={(e) => handleFormChange('min_seats', e.target.value)}
                inputProps={{ min: '1' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Maximum Seats Per Company"
                type="number"
                fullWidth
                margin="normal"
                value={formData.max_seats_per_company}
                onChange={(e) => handleFormChange('max_seats_per_company', e.target.value)}
                inputProps={{ min: '1' }}
              />
            </Grid>
          </Grid>
          
          <TextField
            label="Stripe Product ID"
            fullWidth
            margin="normal"
            value={formData.stripe_product_id}
            onChange={(e) => handleFormChange('stripe_product_id', e.target.value)}
            placeholder="prod_xxxxx"
            helperText="Copy from Stripe Dashboard after creating the product"
          />
          
          <TextField
            label="Stripe Price ID"
            fullWidth
            margin="normal"
            value={formData.stripe_price_id}
            onChange={(e) => handleFormChange('stripe_price_id', e.target.value)}
            placeholder="price_xxxxx"
            helperText="Copy from Stripe Dashboard after creating the price with unit label"
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => handleFormChange('is_active', e.target.checked)}
              />
            }
            label="Active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitCreate} 
            variant="contained" 
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={handleCloseEditDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Edit Live Agent Product</DialogTitle>
        <DialogContent>
          {updateError && <Alert severity="error" sx={{ mb: 2 }}>{updateError}</Alert>}
          
          <TextField
            label="Product Name"
            fullWidth
            margin="normal"
            value={formData.name}
            onChange={(e) => handleFormChange('name', e.target.value)}
            required
          />
          
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
          />
          
          <TextField
            label="Price Per Seat (USD)"
            type="number"
            fullWidth
            margin="normal"
            value={formData.price_per_seat}
            onChange={(e) => handleFormChange('price_per_seat', e.target.value)}
            required
            inputProps={{ step: '0.01', min: '0' }}
            helperText="Note: Changing this won't affect existing subscriptions"
          />
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Minimum Seats"
                type="number"
                fullWidth
                margin="normal"
                value={formData.min_seats}
                onChange={(e) => handleFormChange('min_seats', e.target.value)}
                inputProps={{ min: '1' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Maximum Seats Per Company"
                type="number"
                fullWidth
                margin="normal"
                value={formData.max_seats_per_company}
                onChange={(e) => handleFormChange('max_seats_per_company', e.target.value)}
                inputProps={{ min: '1' }}
              />
            </Grid>
          </Grid>
          
          <TextField
            label="Stripe Product ID"
            fullWidth
            margin="normal"
            value={formData.stripe_product_id}
            onChange={(e) => handleFormChange('stripe_product_id', e.target.value)}
            placeholder="prod_xxxxx"
          />
          
          <TextField
            label="Stripe Price ID"
            fullWidth
            margin="normal"
            value={formData.stripe_price_id}
            onChange={(e) => handleFormChange('stripe_price_id', e.target.value)}
            placeholder="price_xxxxx"
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => handleFormChange('is_active', e.target.checked)}
              />
            }
            label="Active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitUpdate} 
            variant="contained" 
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Delete Live Agent Product?</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography>
            Are you sure you want to delete the Live Agent product configuration?
            This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            You can only delete if there are no active subscriptions.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitDelete} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LiveAgentAdmin;

